import { AxiosResponse } from "axios";
import { axiosInstance } from "./axiosInstance";
import { PagedDTO } from "../../models/WorkshopDTO";
import { AttendeeBody, BajaDestino, BajaPreviewDTO, CheckinDTO, CheckinLogDTO, EligibleListDTO, EnviarGrupoDTO, EventBody, EventStudentDTO, EventDetailDTO, EventGroupDTO, EventResultDTO, InvitationTextDTO, EventListRowDTO, GraduadasDTO, HistoriaDTO, PreviewDTO } from "../../models/EventDTO";

const base = (campusId: number) => `/admin/campus/${campusId}/events`;

/** Eventos (FASE 4): graduaciones, batallas, seminarios. */
export class EventsService {
	list = async (campusId: number, f: { type?: string; status?: string; when?: string; q?: string; page?: number; perPage?: number } = {}): Promise<AxiosResponse<PagedDTO<EventListRowDTO> & { tabCounts?: { [k: string]: number } }>> => {
		const params: { [k: string]: string | number } = {};
		Object.entries(f).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") params[k] = v as any; });
		return axiosInstance.get(base(campusId), { params });
	}

	details = async (campusId: number, id: number): Promise<AxiosResponse<EventDetailDTO>> =>
		axiosInstance.get<EventDetailDTO>(`${base(campusId)}/_details`, { params: { id } });

	create = async (campusId: number, body: EventBody): Promise<AxiosResponse<EventDetailDTO>> => axiosInstance.post<EventDetailDTO>(base(campusId), body);

	update = async (campusId: number, body: Partial<EventBody> & { id: number }): Promise<AxiosResponse<EventDetailDTO>> => axiosInstance.put<EventDetailDTO>(base(campusId), body);

	eligible = async (campusId: number, eventId: number, courseId?: number): Promise<AxiosResponse<EligibleListDTO>> =>
		axiosInstance.get<EligibleListDTO>(`${base(campusId)}/_eligible`, { params: { eventId, courseId: courseId || undefined } });

	addEligible = async (campusId: number, eventId: number, enrollmentIds: number[], charge?: boolean): Promise<AxiosResponse<{ added: number; skipped: any[] }>> =>
		axiosInstance.post(`${base(campusId)}/_eligible`, { eventId, enrollmentIds, charge });

	/** Alumnas para agregar a mano: las del plantel y las de Online que caben en este evento. */
	students = async (campusId: number, eventId: number, q: string): Promise<AxiosResponse<EventStudentDTO[]>> =>
		axiosInstance.get<EventStudentDTO[]>(`${base(campusId)}/_students`, { params: { eventId, q } });

	addAttendee = async (campusId: number, body: AttendeeBody): Promise<AxiosResponse<any>> => axiosInstance.post(`${base(campusId)}/_attendees`, body);

	updateAttendee = async (campusId: number, body: { attendeeId: number; tableNo?: string | null; name?: string; paid?: boolean; price?: number; priceNote?: string; note?: string }): Promise<AxiosResponse<any>> =>
		axiosInstance.put(`${base(campusId)}/_attendee`, body);

	/** Extras (2026-10-25): vender, marcar entregado, quitar. Los tres devuelven el detalle del evento. */
	addExtra = async (campusId: number, attendeeId: number, key: number): Promise<AxiosResponse<EventDetailDTO>> =>
		axiosInstance.post<EventDetailDTO>(`${base(campusId)}/_extras`, { attendeeId, key });

	deliverExtra = async (campusId: number, extraId: number, delivered: boolean): Promise<AxiosResponse<EventDetailDTO>> =>
		axiosInstance.put<EventDetailDTO>(`${base(campusId)}/_extra`, { extraId, delivered });

	removeExtra = async (campusId: number, extraId: number): Promise<AxiosResponse<EventDetailDTO>> =>
		axiosInstance.put<EventDetailDTO>(`${base(campusId)}/_removeExtra`, { extraId });

	/** Reparte mesas por personas por mesa (R2); keepManual respeta las puestas a mano. Devuelve el detalle. */
	assignTables = async (campusId: number, eventId: number, keepManual = false): Promise<AxiosResponse<EventDetailDTO>> =>
		axiosInstance.post<EventDetailDTO>(`${base(campusId)}/_tables`, { eventId, keepManual });

	/** Lo que pasaría con el dinero al dar de baja (fase 2): quiénes salen, pagado, retenido y sobrante. */
	bajaPreview = async (campusId: number, attendeeId: number): Promise<AxiosResponse<BajaPreviewDTO>> =>
		axiosInstance.get<BajaPreviewDTO>(`${base(campusId)}/_bajaPreview`, { params: { attendeeId } });

	/** Da de baja también a sus invitados. destino es obligatorio cuando sobra dinero. Devuelve el detalle. */
	removeAttendee = async (campusId: number, attendeeId: number, note?: string, destino?: BajaDestino): Promise<AxiosResponse<EventDetailDTO>> =>
		axiosInstance.put<EventDetailDTO>(`${base(campusId)}/_removeAttendee`, { attendeeId, note, destino });

	/** Invitaciones de grupo por graduada (fase 2). */
	grupos = async (campusId: number, eventId: number): Promise<AxiosResponse<EventGroupDTO[]>> =>
		axiosInstance.get<EventGroupDTO[]>(`${base(campusId)}/_grupos`, { params: { eventId } });

	/** Manda la invitación del grupo por WhatsApp y correo; 422 si la graduada no ha pagado todo. */
	enviarGrupo = async (campusId: number, hostAttendeeId: number): Promise<AxiosResponse<EnviarGrupoDTO>> =>
		axiosInstance.post<EnviarGrupoDTO>(`${base(campusId)}/_enviarGrupo`, { hostAttendeeId });

	/** 409 segunda lectura, 404 código, 422 baja: el mensaje viene en data.message. */
	checkin = async (campusId: number, qrCode: string): Promise<AxiosResponse<CheckinDTO>> => axiosInstance.post<CheckinDTO>(`${base(campusId)}/_checkin`, { qrCode });

	reenable = async (campusId: number, attendeeId: number, note: string): Promise<AxiosResponse<any>> => axiosInstance.put(`${base(campusId)}/_reenable`, { attendeeId, note });

	checkins = async (campusId: number, attendeeId: number): Promise<AxiosResponse<CheckinLogDTO[]>> =>
		axiosInstance.get<CheckinLogDTO[]>(`${base(campusId)}/_checkins`, { params: { attendeeId } });

	uploadPhoto = async (campusId: number, attendeeId: number, photo: File): Promise<AxiosResponse<any>> => {
		const form = new FormData();
		form.append("attendeeId", String(attendeeId));
		form.append("photo", photo);
		return axiosInstance.post(`${base(campusId)}/_photo`, form, { headers: { "Content-Type": "multipart/form-data" } });
	}

	photo = async (campusId: number, attendeeId: number): Promise<AxiosResponse<Blob>> =>
		axiosInstance.get(`${base(campusId)}/_photo`, { params: { attendeeId }, responseType: "blob" });

	invitationPdf = async (campusId: number, attendeeId: number): Promise<AxiosResponse<Blob>> =>
		axiosInstance.get(`${base(campusId)}/_invitation`, { params: { attendeeId }, responseType: "blob" });

	/** PDF con todas las invitaciones vigentes del evento, una página por asistente (R2). */
	invitationsPdf = async (campusId: number, id: number): Promise<AxiosResponse<Blob>> =>
		axiosInstance.get(`${base(campusId)}/_invitations`, { params: { id }, responseType: "blob" });

	invitationText = async (campusId: number, attendeeId: number): Promise<AxiosResponse<InvitationTextDTO>> =>
		axiosInstance.get<InvitationTextDTO>(`${base(campusId)}/_invitationText`, { params: { attendeeId } });

	invitationTexts = async (campusId: number, eventId: number): Promise<AxiosResponse<{ eventId: number; rows: InvitationTextDTO[] }>> =>
		axiosInstance.get(`${base(campusId)}/_invitationText`, { params: { eventId } });

	/** 422 "Falta: foto, asistencia, pago." */
	diplomaPdf = async (campusId: number, attendeeId: number): Promise<AxiosResponse<Blob>> =>
		axiosInstance.get(`${base(campusId)}/_diploma`, { params: { attendeeId }, responseType: "blob" });

	/** Módulos Claros: una fila por graduada con su grupo, su expediente y el «Qué Va a Pasar». */
	graduadas = async (campusId: number, eventId: number): Promise<AxiosResponse<GraduadasDTO>> =>
		axiosInstance.get<GraduadasDTO>(`${base(campusId)}/_graduadas`, { params: { eventId } });

	historial = async (campusId: number, attendeeId: number): Promise<AxiosResponse<HistoriaDTO[]>> =>
		axiosInstance.get<HistoriaDTO[]>(`${base(campusId)}/_historial`, { params: { attendeeId } });

	/** Altas (mismos cuerpos que POST _attendees, en el mismo orden) o venta de un extra: no cambia nada. */
	previewAdd = async (campusId: number, body: { eventId: number; action: "attendees"; items: Omit<AttendeeBody, "eventId">[]; lenient?: boolean } | { eventId: number; action: "extra"; attendeeId: number; key: number }): Promise<AxiosResponse<PreviewDTO>> =>
		axiosInstance.post<PreviewDTO>(`${base(campusId)}/_preview`, body);

	/** Guardar cambios del evento o anularlo: no cambia nada. */
	previewUpdate = async (campusId: number, body: { eventId: number; action: "update"; body: Partial<EventBody> } | { eventId: number; action: "anular" }): Promise<AxiosResponse<PreviewDTO>> =>
		axiosInstance.put<PreviewDTO>(`${base(campusId)}/_preview`, body);

	result = async (campusId: number, id: number): Promise<AxiosResponse<EventResultDTO>> => axiosInstance.get<EventResultDTO>(`${base(campusId)}/_result`, { params: { id } });
}

const eventsService = new EventsService();
export default eventsService;
